import * as signalR from '@microsoft/signalr';

const HUB_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/hubs/stream`
  : '/hubs/stream';

class SignalRService {
  constructor() {
    this.connection = null;
    this.listeners = new Map();
    this.connectionPromise = null;
  }

  async connect() {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return this.connection;
    }

    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = this._createConnection();
    return this.connectionPromise;
  }

  async _createConnection() {
    const token = localStorage.getItem('authToken');

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL, {
        accessTokenFactory: () => token || '',
        skipNegotiation: false,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: retryContext => {
          // Exponential backoff: 0s, 2s, 10s, 30s
          if (retryContext.previousRetryCount === 0) return 0;
          if (retryContext.previousRetryCount === 1) return 2000;
          if (retryContext.previousRetryCount === 2) return 10000;
          return 30000;
        }
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // Connection lifecycle events
    this.connection.onclose((error) => {
      console.log('SignalR connection closed', error);
      this.connectionPromise = null;
    });

    this.connection.onreconnecting((error) => {
      console.log('SignalR reconnecting...', error);
    });

    this.connection.onreconnected((connectionId) => {
      console.log('SignalR reconnected', connectionId);
      // Re-subscribe to all streams after reconnection
      this._resubscribeAll();
    });

    try {
      await this.connection.start();
      console.log('SignalR connected');
      return this.connection;
    } catch (error) {
      console.error('SignalR connection error:', error);
      this.connectionPromise = null;
      throw error;
    }
  }

  async disconnect() {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
      this.connectionPromise = null;
    }
  }

  async subscribeToStream(streamId, callback) {
    await this.connect();

    const eventName = 'stream:status';
    const wrappedCallback = (data) => {
      if (data.streamId === streamId) {
        callback(data);
      }
    };

    // Register listener for this specific stream
    this.connection.on(eventName, wrappedCallback);

    // Invoke server method to join group
    try {
      await this.connection.invoke('SubscribeToStream', streamId);
      console.log(`Subscribed to stream ${streamId}`);
    } catch (error) {
      console.error(`Failed to subscribe to stream ${streamId}:`, error);
    }

    // Store listener for cleanup and reconnection
    const key = `${streamId}:${eventName}`;
    this.listeners.set(key, { streamId, callback: wrappedCallback });

    return () => this.unsubscribeFromStream(streamId);
  }

  async unsubscribeFromStream(streamId) {
    if (!this.connection) return;

    const eventName = 'stream:status';
    const key = `${streamId}:${eventName}`;
    const listener = this.listeners.get(key);

    if (listener) {
      this.connection.off(eventName, listener.callback);
      this.listeners.delete(key);
    }

    // Invoke server method to leave group
    try {
      await this.connection.invoke('UnsubscribeFromStream', streamId);
      console.log(`Unsubscribed from stream ${streamId}`);
    } catch (error) {
      console.error(`Failed to unsubscribe from stream ${streamId}:`, error);
    }
  }

  async _resubscribeAll() {
    console.log('Re-subscribing to all streams after reconnection...');
    for (const [key, { streamId }] of this.listeners.entries()) {
      try {
        await this.connection.invoke('SubscribeToStream', streamId);
        console.log(`Re-subscribed to stream ${streamId}`);
      } catch (error) {
        console.error(`Failed to re-subscribe to stream ${streamId}:`, error);
      }
    }
  }

  // --- Audience Interaction Methods ---

  async sendReaction(streamId, reactionType) {
    await this.connect();
    try {
      await this.connection.invoke('SendReaction', streamId, reactionType);
    } catch (error) {
      console.error('Failed to send reaction:', error);
    }
  }

  async sendMessage(streamId, text) {
    await this.connect();
    try {
      await this.connection.invoke('SendMessage', streamId, text);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  }

  async sendPerformerMessage(streamId, text) {
    await this.connect();
    try {
      await this.connection.invoke('SendPerformerMessage', streamId, text);
    } catch (error) {
      console.error('Failed to send performer message:', error);
    }
  }

  onReaction(streamId, callback) {
    return this._onStreamEvent('stream:reaction', streamId, callback);
  }

  onMessage(streamId, callback) {
    return this._onStreamEvent('stream:message', streamId, callback);
  }

  onViewerCount(streamId, callback) {
    return this._onStreamEvent('stream:viewerCount', streamId, callback);
  }

  async _onStreamEvent(eventName, streamId, callback) {
    await this.connect();

    const wrappedCallback = (data) => {
      if (data.streamId === streamId) {
        callback(data);
      }
    };

    this.connection.on(eventName, wrappedCallback);

    const key = `${streamId}:${eventName}`;
    this.listeners.set(key, { streamId, callback: wrappedCallback, eventName });

    return () => {
      if (this.connection) {
        this.connection.off(eventName, wrappedCallback);
      }
      this.listeners.delete(key);
    };
  }

  // --- Generic Methods ---

  async on(event, callback) {
    await this.connect();
    this.connection.on(event, callback);

    return () => {
      if (this.connection) {
        this.connection.off(event, callback);
      }
    };
  }

  async invoke(method, ...args) {
    await this.connect();
    return this.connection.invoke(method, ...args);
  }
}

export const signalRService = new SignalRService();
export default signalRService;
